import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Divider, Loader, Stack } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import useLearningPath from '@/modules/corporate/modules/path/hooks/useLearningPath';
import { ContentEntity, DueDateNestingLevelEnum, PathSectionType } from '@/types/schema';

import { RenderRemoveDueDate } from '../../renderer';
import { AdaptedNestedDueDateConfig } from '../ContentAndCollectionList/hooks/useContentAndCollectionList';
import { nestedDialogClass } from '../SetDueDateDialog/styles';
import Section from './Section';

interface NestedPathModalProps {
  actionPrimaryText: string;
  title: string;
  description: string;
  content: ContentEntity;
  onCloseDueDateDialog: () => void;
  dueDateNestingLevel: DueDateNestingLevelEnum;
  onSetDueDateForContent: (
    content: ContentEntity,
    dueDateNestingLevel?: DueDateNestingLevelEnum | undefined,
    isNested?: boolean,
  ) => void;
  onSetDueDateForSection: (
    section: PathSectionType,
    dueDateNestingLevel?: DueDateNestingLevelEnum | undefined,
    isNested?: boolean,
  ) => void;
  nestedDueDateConfig: AdaptedNestedDueDateConfig;
  onSetNestedDueDate: () => void;
  onReset: () => void;
}

function NestedPathModal(props: NestedPathModalProps) {
  const { t } = useTranslation();
  const {
    actionPrimaryText,
    title,
    description,
    onCloseDueDateDialog,
    content,
    dueDateNestingLevel,
    onSetDueDateForContent,
    onSetDueDateForSection,
    nestedDueDateConfig,
    onSetNestedDueDate,
    onReset,
  } = props;

  const { fetching, path } = useLearningPath({
    pathId: content.id,
  });

  return (
    <Modal
      action={{
        text: actionPrimaryText,
        onSubmit: onSetNestedDueDate,
        TertiaryAction: RenderRemoveDueDate(t, onReset),
      }}
      classes={nestedDialogClass}
      header={{
        title,
        description,
      }}
      variant={ModalVariant.FullHeight}
      onClose={onCloseDueDateDialog}>
      <Stack className="w-full">
        {fetching ? (
          <Stack alignItems="center" className="h-256 w-full" justifyContent="center">
            <Loader />
          </Stack>
        ) : (
          <Stack className="w-full" direction="col">
            {path?.sections?.map((section, index) => (
              <>
                {index > 0 ? <Divider className="mb-20" /> : null}
                <Section
                  key={section.id}
                  dueDateNestingLevel={dueDateNestingLevel}
                  nestedDueDateConfig={nestedDueDateConfig}
                  section={section}
                  onSetDueDateForContent={onSetDueDateForContent}
                  onSetDueDateForSection={onSetDueDateForSection}
                />
              </>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}

export default NestedPathModal;
